import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio } from "lucide-react";

function getHeroHeadlineLines(slide) {
  if (slide.titleLines?.length) return slide.titleLines;

  const lines = [];
  const title = slide.title?.trim() || "";
  const highlight = slide.highlight?.trim() || "";
  const commaIndex = title.indexOf(",");

  if (commaIndex !== -1) {
    lines.push(title.slice(0, commaIndex + 1).trim());
    lines.push(title.slice(commaIndex + 1).trim());
  } else if (title) {
    lines.push(title);
  }

  if (highlight) lines.push(highlight);
  return lines;
}

function HeroPlayIcon() {
  return (
    <span className="tv-hero-play-icon" aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M3.25 2.2v5.6l4.5-2.8-4.5-2.8z" fill="#8b5cf6" />
      </svg>
    </span>
  );
}

export default function HeroSlider({ slides }) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, count]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setTimeout(() => {
      slides.slice(1).forEach((s) => {
        if (!s.image) return;
        const img = new Image();
        img.src = s.image;
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [slides]);

  if (!count) return <div className="h-[560px]" />;
  const slide = slides[index];
  const headlineLines = getHeroHeadlineLines(slide);
  const highlightLine = slide.highlight?.trim();

  return (
    <section className="relative h-[580px] md:h-[620px] overflow-hidden bg-[#06060d] isolate">
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt=""
            decoding="async"
            fetchPriority={index === 0 ? "high" : "low"}
            loading={index === 0 ? "eager" : "lazy"}
            className={`w-full h-full object-cover ${index < 3 ? "object-[68%_center]" : "object-center"}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06060d] via-[#06060d]/78 via-[38%] to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06060d]/70 via-transparent to-[#06060d]/25" />
        </motion.div>
      </AnimatePresence>

      <div className="relative tv-container h-full flex items-center pt-[74px] pb-14">
        <div className="tv-hero-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              <h1 className="tv-hero-title">
                {headlineLines.map((line) => (
                  <span
                    key={line}
                    className={`tv-hero-title-line${line === highlightLine ? " tv-hero-highlight" : ""}`}
                  >
                    {line}
                  </span>
                ))}
              </h1>
              <p className="tv-hero-subtitle">{slide.subtitle}</p>
              <div className="tv-hero-actions">
                <Link to="/programlar" className="tv-hero-btn-primary">
                  <HeroPlayIcon />
                  Programları İncele
                </Link>
                <Link to="/canli-yayin" className="tv-hero-btn-ghost">
                  <Radio size={17} strokeWidth={1.75} />
                  Canlı Yayına Git
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="absolute bottom-7 md:bottom-8 left-0 right-0 flex justify-center gap-2.5">
        {slides.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setIndex(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-[var(--tv-purple-bright)]" : "w-2.5 bg-white/35 hover:bg-white/55"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
